'use client';

import { useState, useEffect, useRef } from 'react';
import { saveConsent, getConsent, logConsentAction } from '@/lib/consent';

interface ConsentPreferenceCenterProps {
  region: 'gdpr' | 'ccpa' | 'none';
  onClose: () => void;
}

export default function ConsentPreferenceCenter({
  region,
  onClose
}: ConsentPreferenceCenterProps) {
  const [choices, setChoices] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false
  });
  const [initialChoices, setInitialChoices] = useState(choices);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const lastFocusableRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const existingConsent = getConsent();
    if (existingConsent) {
      setChoices(existingConsent.choices);
      setInitialChoices(existingConsent.choices);
    }

    // Focus trap
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }

      if (e.key === 'Tab') {
        const focusableElements = dialogRef.current?.querySelectorAll(
          'button, [role="switch"]'
        );
        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    firstFocusableRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handleToggle = (category: 'analytics' | 'marketing' | 'preferences') => {
    setChoices((prev) => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSave = () => {
    saveConsent(choices, region);
    logConsentAction('preferences_saved', { choices, region });
    onClose();
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    setChoices(allAccepted);
    saveConsent(allAccepted, region);
    logConsentAction('accept_all_from_preferences', { region });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="preferences-title"
        className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-8">
          <h2 id="preferences-title" className="text-2xl font-bold text-brand-primary mb-6">
            Nastavení cookies
          </h2>

          <div className="space-y-6 mb-8">
            {/* Necessary Cookies */}
            <div className="border-b pb-6">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">🔒 Nezbytné cookies</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Zajišťují základní funkčnost webu. Nelze vypnout.
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked
                  disabled
                  className="ml-4 w-12 h-7 bg-accent rounded-full flex items-center justify-end px-1 cursor-not-allowed"
                >
                  <div className="w-5 h-5 bg-white rounded-full" />
                </button>
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="border-b pb-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">📊 Analytické cookies</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Pomáhají nám pochopit, jak web používáš (např. Google Analytics).
                  </p>
                </div>
                <button
                  ref={firstFocusableRef}
                  role="switch"
                  aria-checked={choices.analytics}
                  onClick={() => handleToggle('analytics')}
                  className={`ml-4 w-12 h-7 rounded-full flex items-center transition-colors ${
                    choices.analytics
                      ? 'bg-accent justify-end'
                      : 'bg-gray-300 justify-start'
                  }`}
                >
                  <div className="w-5 h-5 bg-white rounded-full" />
                </button>
              </div>
            </div>

            {/* Marketing Cookies */}
            <div className="border-b pb-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">🎯 Marketingové cookies</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Umožňují zobrazovat relevantní reklamy (např. Meta / Facebook).
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked={choices.marketing}
                  onClick={() => handleToggle('marketing')}
                  className={`ml-4 w-12 h-7 rounded-full flex items-center transition-colors ${
                    choices.marketing
                      ? 'bg-accent justify-end'
                      : 'bg-gray-300 justify-start'
                  }`}
                >
                  <div className="w-5 h-5 bg-white rounded-full" />
                </button>
              </div>
            </div>

            {/* Preferences Cookies */}
            <div>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">⚙️ Preferenční cookies</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Zapamatují si tvoje preference a nastavení.
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked={choices.preferences}
                  onClick={() => handleToggle('preferences')}
                  className={`ml-4 w-12 h-7 rounded-full flex items-center transition-colors ${
                    choices.preferences
                      ? 'bg-accent justify-end'
                      : 'bg-gray-300 justify-start'
                  }`}
                >
                  <div className="w-5 h-5 bg-white rounded-full" />
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-6 py-3 text-gray-700 font-semibold rounded border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              Zrušit
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-3 bg-gray-700 text-white font-semibold rounded hover:bg-gray-800 transition-colors"
            >
              Uložit nastavení
            </button>
            <button
              ref={lastFocusableRef}
              onClick={handleAcceptAll}
              className="px-6 py-3 bg-accent text-brand-primary font-semibold rounded hover:bg-accent/90 transition-colors"
            >
              Přijmout vše
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
